import { useState, useEffect } from 'react';
import { api } from '../../../utils/api';

/**
 * `itemName` is Prism's `display_name` for the item — the name STORED beside an
 * exclusion, which is what the "item was renamed (was: …)" line reads back. It
 * is not the attachment key: that is derived server-side from the deal's own
 * parsed line, deliberately, because aligned line parsers have drifted before
 * (see the POST comp-exclusions route). Handed down whole rather than rebuilt
 * from the rendered name (pricing-worker `docs/specs_20260727_item_naming.md`).
 */
export default function MarketCompsPanel({ dealId, quoteItemId, itemName, refreshKey, onExclusionsChanged }) {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [actionError, setActionError] = useState(null);
    const [showExcluded, setShowExcluded] = useState(false);
    const [localBump, setLocalBump] = useState(0);

    useEffect(() => {
        let alive = true;
        setData(null); setError(null); setActionError(null); setShowExcluded(false);
        api.get(`/api/deals/${dealId}/items/${quoteItemId}/market-comparables`)
            .then(r => { if (alive) setData(r); })
            .catch(e => { if (alive) setError(e.message || 'Failed to load comps'); });
        return () => { alive = false; };
    }, [dealId, quoteItemId, refreshKey, localBump]);

    if (error) return <div className="qa-error" role="alert">{error}</div>;
    if (!data) return <div className="qa-muted">Loading comps…</div>;
    if (!data.listings.length) return <div className="qa-muted">No market data for this item.</div>;

    const b = data.basis;
    const soldN = b ? (b.ebay_sold_count || 0) + (b.reverb_sold_count || 0) : 0;
    const activeN = b ? (b.ebay_active_count || 0) + (b.reverb_active_count || 0) : 0;

    // Comps the classifier could not label are dropped from pricing (spec 20260910
    // classifier_partial_visibility). Older runs carry no count; null and 0 mean none.
    const classifierDroppedN = b?.classifier_dropped_count > 0 ? b.classifier_dropped_count : 0;

    const exclusions = data.exclusions || [];
    const dormant = data.dormantExclusions || [];
    const pending = data.pendingExclusions || 0;
    // Marked rows drop out of the included list immediately ("moved" to the
    // excluded-by-you section); run rows already excluded as 'operator' are the
    // same marks after recalculation — the exclusions list renders them, so keep
    // them out of the generic excluded toggle to avoid double display.
    const included = data.listings.filter(l => l.included && !l.operator_excluded);
    const excluded = data.listings.filter(l => !l.included && l.exclusion_reason !== 'operator');
    const includedSold = included.filter(l => /sold/i.test(l.source_bucket || l.source || '')).length;
    // International sold comps enter pricing at half weight (pricing-worker spec
    // 20260820_ebay_sold_intl); the engine's sold count can omit them on the keyword
    // path, so count them from the rows the operator is looking at.
    const intlSoldN = included.filter(l => l.geo === 'intl' && /sold/i.test(l.source_bucket || l.source || '')).length;
    // Engine weight chip: only when it is not the full 1.0 (intl 0.5, unknown-geo 0.75).
    const weightOf = (l) => (l.included && l.geo_weight != null && Number(l.geo_weight) > 0 && Number(l.geo_weight) !== 1) ? Number(l.geo_weight) : null;

    const mark = async (l) => {
        setActionError(null);
        try {
            await api.post(`/api/deals/${dealId}/items/${quoteItemId}/comp-exclusions`, {
                item_name: itemName || null, listing_url: l.listing_url || null,
                source: l.source, title: l.title, price: l.price,
            });
            setLocalBump(v => v + 1);
            onExclusionsChanged?.();
        } catch (e) { setActionError(e.message || 'Failed to save exclusion'); }
    };
    const undo = async (exclusionId) => {
        setActionError(null);
        try {
            await api.delete(`/api/deals/${dealId}/items/${quoteItemId}/comp-exclusions/${exclusionId}`);
            setLocalBump(v => v + 1);
            onExclusionsChanged?.();
        } catch (e) { setActionError(e.message || 'Failed to undo exclusion'); }
    };

    // A comp's price is item + postage (the engine's effective price). Show the split so a
    // wrong postage is visible in the panel, not only in the debug log: TI-6B0CE406
    // (2026-09-10) priced from £19.99 + £46.46 international postage shown as £66.45.
    // Postage at or above the item price is flagged; no engine estimate needed for that.
    const renderPostage = (l) => {
        const postage = Number(l.shipping_cost);
        if (!(postage > 0) || l.price == null) return null;
        const item = Math.round((Number(l.price) - postage) * 100) / 100;
        const bad = postage >= item;
        return (
            <span
                className={`qa-comp-postage ${bad ? 'qa-comp-postage-bad' : ''}`}
                title={bad ? 'Postage exceeds item price — check the source' : 'Item price + postage'}
            >
                {`£${item.toFixed(2)} + £${postage.toFixed(2)} post`}
            </span>
        );
    };

    const renderComp = (l, key, { markable = false } = {}) => (
        <div key={key} className={`qa-comp ${l.included && !l.operator_excluded ? '' : 'qa-comp-excluded'}`}>
            <span className={`qa-comp-src ${/sold/i.test(l.source_bucket || '') ? 'qa-comp-sold' : 'qa-comp-active'}`}>
                {(l.source_bucket || l.source || '').replace(/-/g, ' ')}
            </span>
            {/* Engine verdict (is_new), not a condition-string guess — open box/refurb
                differ per source (spec 20260804, pricing-worker). */}
            {!!l.is_new && <span className="qa-comp-new" title={l.condition || 'New / retail listing'}>NEW</span>}
            {l.listing_url
                ? <a href={l.listing_url} target="_blank" rel="noreferrer">{l.title}</a>
                : <span>{l.title}</span>}
            {l.sold_date && <span className="qa-comp-date" title="Sold date">📅 {l.sold_date}</span>}
            <span className={`qa-comp-geo ${l.geo === 'intl' ? 'qa-comp-intl' : ''}`} title="Listing location">
                {l.geo === 'uk' ? '🇬🇧 UK' : l.geo === 'intl' ? '🌍 Int’l' : ''}
            </span>
            {weightOf(l) != null && (
                <span className="qa-comp-weight" title="Engine weight — this comp counts less than a UK sale">×{weightOf(l)}</span>
            )}
            <span className="qa-comp-price">£{l.price == null ? '—' : Math.round(Number(l.price) * 100) / 100}</span>
            {renderPostage(l)}
            {/* Flag column only on excluded rows, where it carries the reason —
                a ✓ on every included row said nothing (removed 2026-07-15, Zsolt). */}
            {!(l.included && !l.operator_excluded) && (
                <span className="qa-comp-flag">{l.exclusion_reason || '✗'}</span>
            )}
            {markable && (
                <button type="button" className="qa-comp-x" title="Exclude this comp" onClick={() => mark(l)}>✗</button>
            )}
        </div>
    );

    return (
        <div className="qa-comps">
            <div className="qa-comps-head">📊 Market comps <span className="qa-muted">used {data.usedCount} of {data.totalCount}{pending > 0 && <> · {pending} pending exclusion{pending === 1 ? '' : 's'}</>}</span></div>
            {actionError && <div className="qa-error" role="alert">{actionError}</div>}
            {pending > 0 && (
                // Informational only — the per-item £ button is the reprice entry
                // point (amended 2026-07-15, Zsolt: no second reprice button).
                <div className="qa-comps-nudge" role="status">
                    ⚠ Price predates {pending} of your exclusion{pending === 1 ? '' : 's'} — reprice (£) to apply.
                </div>
            )}
            {classifierDroppedN > 0 && (
                // Informational only: the existing Reprice action is the retry.
                <div className="qa-comps-nudge qa-banner-warning" role="status">
                    {classifierDroppedN === 1
                        ? '1 comp was dropped because the classifier timed out. Reprice this item.'
                        : `${classifierDroppedN} comps were dropped because the classifier timed out. Reprice this item.`}
                </div>
            )}
            {data.searchTerm && (
                <div className="qa-comps-query">
                    Search term used: <span className="qa-query-term">“{data.searchTerm}”</span>
                    {data.sparseFallback && <span className="qa-muted"> · broad fallback</span>}
                </div>
            )}
            {b && (
                <div className="qa-comps-basis">
                    <div className="qa-basis-anchor">{b.anchor_logic || 'pricing basis n/a'}</div>
                    <div className="qa-muted">
                        sold {soldN}{soldN === 0 ? ' ⚠' : ''}{intlSoldN > 0 ? ` (${intlSoldN} int’l)` : ''} · active {activeN} · google {b.google_used_count || 0} · new {b.new_price_count || 0} · score {b.dataset_score ?? '—'}
                    </div>
                </div>
            )}
            {exclusions.length > 0 && includedSold < 3 && (
                <div className="qa-error" role="alert">⚠ Only {includedSold} sold comp{includedSold === 1 ? '' : 's'} left after your exclusions — thin dataset.</div>
            )}
            {included.map((l, i) => renderComp(l, `inc-${i}`, { markable: true }))}
            {exclusions.length > 0 && (
                <div className="qa-comps-yours">
                    <div className="qa-muted">excluded by you — takes effect on recalculation</div>
                    {exclusions.map(ex => (
                        <div key={`ex-${ex.id}`} className="qa-comp qa-comp-excluded">
                            <span className="qa-comp-src">{(ex.source || '').replace(/-/g, ' ')}</span>
                            {ex.listing_url ? <a href={ex.listing_url} target="_blank" rel="noreferrer">{ex.title}</a> : <span>{ex.title}</span>}
                            <span className="qa-comp-price">£{ex.price_cents == null ? '—' : Math.round(ex.price_cents) / 100}</span>
                            <span className="qa-comp-flag">operator</span>
                            <button type="button" className="qa-comp-x" title="Undo exclusion" onClick={() => undo(ex.id)}>undo</button>
                        </div>
                    ))}
                </div>
            )}
            {dormant.length > 0 && (
                <div className="qa-comps-dormant qa-muted">
                    <div>{dormant.length} exclusion{dormant.length === 1 ? '' : 's'} dormant — item was renamed (was: {dormant[0].item_name || 'unknown'})</div>
                    {dormant.map(ex => (
                        <div key={`dor-${ex.id}`} className="qa-comp qa-comp-excluded" style={{ opacity: 0.6 }}>
                            <span className="qa-comp-src">{(ex.source || '').replace(/-/g, ' ')}</span>
                            <span>{ex.title}</span>
                            <button type="button" className="qa-comp-x" title="Delete dormant exclusion" onClick={() => undo(ex.id)}>remove</button>
                        </div>
                    ))}
                </div>
            )}
            {excluded.length > 0 && (
                <button type="button" className="qa-comps-toggle" onClick={() => setShowExcluded(s => !s)}>
                    {showExcluded ? `▾ Hide ${excluded.length} excluded` : `▸ Show ${excluded.length} excluded`}
                </button>
            )}
            {showExcluded && excluded.map((l, i) => renderComp(l, `exc-${i}`))}
        </div>
    );
}
